import IconArrowRight from '@mnd-frontend/icons/components/ArrowRight';
import {
  BREAKPOINTS,
  RADIUS_LG,
  RADIUS_XL,
  SIZING_0_5,
  SIZING_2,
  SIZING_3,
  SPACING_0_5,
  SPACING_1,
  SPACING_2,
  SPACING_3,
  WEB_GREY_LT,
  WEB_GREY_REG,
  WEB_RED_MID,
  WEB_RED_REG,
} from '@mnd-frontend/ui/shared/tokens';
import NextLink from 'next/link';
import styled from 'styled-components';
import type { formatCardMenuItem } from '../../../utils/formatters/menu';

// #region Card top section
const MainNavLink = styled(NextLink)`
  display: block;
  &:hover {
    color: ${WEB_RED_REG};
  }
`;

const MainNavHeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  &+.description {
    margin-top: ${SPACING_1};
  }
`;

const TopNavLabel = styled.div`
  /* web/h3/mobile */
  font-size: ${SIZING_2};
  font-weight: 500;
  line-height: 120%;

  @media (min-width: ${BREAKPOINTS.XL}px) {
    font-size: ${SIZING_3};
  }
`;

const NoLinkMain = styled.a`
  display: block;
  color: ${WEB_RED_MID};
  border-left: ${SIZING_0_5} solid ${WEB_RED_MID};
  padding-left: ${SPACING_1};

  @media (min-width: ${BREAKPOINTS.XL}px) {
    padding-left: ${SPACING_2};
  }
`;

const MainNavSection = ({
  heading,
  description,
  href,
  isCurrentPath,
  onClick,
  disableKeybordNavigation,
}: Omit<CardMenuItemProps, 'subMenuList' | 'currentPath'> & {
  isCurrentPath: boolean;
  onClick?: () => void;
}) => {
  if (!href) return null;
  return isCurrentPath ? (
    <NoLinkMain aria-current="true">
      <MainNavHeadingWrapper>
        <TopNavLabel>{heading}</TopNavLabel>
        <IconArrowRight $size="xs" aria-hidden />
      </MainNavHeadingWrapper>
      {description && <p className="description">{description}</p>}
    </NoLinkMain>
  ) : (
    <MainNavLink href={href} tabIndex={disableKeybordNavigation ? -1 : 0} onClick={onClick}>
      <MainNavHeadingWrapper>
        <TopNavLabel>{heading}</TopNavLabel>
        <IconArrowRight $size="xs" aria-hidden />
      </MainNavHeadingWrapper>
      {description && <p className="description">{description}</p>}
    </MainNavLink>
  );
};
// #endregion

// #region Card sub section
const Card = styled.div`
  background: ${WEB_GREY_LT};
  border-radius: ${RADIUS_LG};
  padding: ${SPACING_2};

  @media (min-width: ${BREAKPOINTS.XL}px) {
    border-radius: ${RADIUS_XL};
    padding: ${SPACING_3};
  }
`;

const SubNavList = styled.ul`
  padding-top: ${SPACING_1};
  margin-top: ${SPACING_2};
  border-top: 1px solid ${WEB_GREY_REG};
  list-style: none;
  margin-top: ${SPACING_2};
  padding: ${SPACING_2} 0 0;

  @media (min-width: ${BREAKPOINTS.XL}px) {
    margin-top: ${SPACING_3};
  }

  .card-list-item {
    font-size: 0.875rem;
    font-weight: 500;
  
    &:not(:last-of-type) {
      margin-bottom: ${SPACING_0_5};
    }
    
    .link-item {
      display: block;
      padding: ${SPACING_1} 0;

      &:hover {
        color: ${WEB_RED_REG};
      }

      @media (min-width: ${BREAKPOINTS.XL}px) {
        padding: ${SPACING_0_5} 0;
        font-size: 1rem;
      }
    }
  }
`;

const NoLinkSub = styled.a`
  display: block;
  padding: ${SPACING_1} 0;
  color: ${WEB_RED_MID};
  border-left: ${SIZING_0_5} solid ${WEB_RED_MID};
  padding-left: ${SPACING_1};

  @media (min-width: ${BREAKPOINTS.XL}px) {
    padding: ${SPACING_0_5} 0 ${SPACING_0_5} ${SPACING_2};
    font-size: 1rem;
  }
`;

type SubMenuListProps = {
  items: {
    label: string;
    href: string;
    onClick?: () => void;
  }[];
};

const SubNavSection = ({
  subMenuList,
  currentPath,
  disableKeybordNavigation = false,
}: {
  subMenuList: SubMenuListProps;
  currentPath: string;
  disableKeybordNavigation?: boolean;
}) => {
  return (
    <SubNavList>
      {subMenuList?.items.map((item, idx) => (
        <li key={idx} className="card-list-item">
          {currentPath === item.href ? (
            <NoLinkSub>{item.label}</NoLinkSub>
          ) : (
            <NextLink
              className="link-item"
              href={item.href}
              onClick={item?.onClick}
              tabIndex={disableKeybordNavigation ? -1 : 0}
            >
              {item.label}
            </NextLink>
          )}
        </li>
      ))}
    </SubNavList>
  );
};
// #endregion
// #region Card
type CardMenuItemProps = {
  heading: string | null | undefined;
  href: string | null | undefined;
  currentPath: string;
  mainNavOnClick?: () => void;
  as?: 'li' | 'div';
  subMenuList?: {
    items: ReturnType<typeof formatCardMenuItem>;
  } | null;
  description?: string | null;
  disableKeybordNavigation?: boolean;
};

const CardMenuItem = ({
  heading,
  description,
  href,
  as = 'div',
  currentPath,
  subMenuList,
  mainNavOnClick,
  disableKeybordNavigation,
}: CardMenuItemProps) => {
  return (
    <Card as={as}>
      <MainNavSection
        heading={heading}
        description={description}
        href={href}
        onClick={mainNavOnClick}
        isCurrentPath={currentPath === href + '/'}
        disableKeybordNavigation={disableKeybordNavigation}
      />
      {!!(subMenuList && subMenuList.items?.length) && (
        <SubNavSection
          subMenuList={subMenuList as SubMenuListProps}
          currentPath={currentPath}
          disableKeybordNavigation={disableKeybordNavigation}
        />
      )}
    </Card>
  );
};
//#endregion
export default CardMenuItem;
